import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type LocationActionsMenuProps = {
  locationName: string;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  duplicating?: boolean;
};

/** Compact ⋯ menu for location edit / duplicate / delete. */
export function LocationActionsMenu({
  locationName,
  onEdit,
  onDuplicate,
  onDelete,
  duplicating = false,
}: LocationActionsMenuProps) {
  const { t } = useTranslation(['displays', 'common']);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('location.actionsAria', { name: locationName })}
        onClick={() => setOpen((value) => !value)}
        className="rounded-lg border border-line px-2.5 py-2 text-sm font-bold leading-none text-muted hover:border-[var(--gs-accent)] hover:text-ink"
      >
        ⋯
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute end-0 z-20 mt-1.5 min-w-[10.5rem] overflow-hidden rounded-[10px] border border-line bg-panel py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3.5 py-2 text-start text-sm font-semibold text-ink hover:bg-[var(--gs-panel2)]"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            {t('common:actions.edit')}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={duplicating}
            className="block w-full px-3.5 py-2 text-start text-sm font-semibold text-ink hover:bg-[var(--gs-panel2)] disabled:opacity-50"
            onClick={() => {
              setOpen(false);
              onDuplicate();
            }}
          >
            {duplicating ? t('location.duplicating') : t('location.duplicate')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3.5 py-2 text-start text-sm font-semibold hover:bg-[var(--gs-panel2)]"
            style={{ color: 'var(--gs-bad)' }}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            {t('common:actions.delete')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
